import { FC, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { API, Endpoints } from '@brg/api';
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useToasts } from '@brg/ui';
import { LoaderSpinner } from 'shared/modals/artzLegalsModal/LoaderAnimation';
import { LayoutConst } from 'core';

interface FormulaField {
    id: string;
    name: string;
    weight: number;
    fieldKey: string;
}

const FormulaWrapper = styled.div`
    background: #F2F2F2;
    padding: 24px;
    border-radius: 8px;
    margin-top: 24px;
    min-width: 888px;
    .ui-input_container{
       background: #FFF; 
    }
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        padding: 20px;
        min-width: auto;
        margin-top: 20px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        padding: 16px;
        margin-top: 16px;
    }
`;

const TableHeader = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 12px;
        margin-bottom: 14px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        gap: 8px;
        margin-bottom: 12px;
        flex-direction: column;
    }
`;

const HeaderLabel = styled.div`
    font-size: 14px;
    color: #666;
    font-weight: 500;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 13px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        font-size: 12px;
    }
`;

const TableRow = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 12px;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 12px;
        margin-bottom: 10px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        gap: 8px;
        margin-bottom: 8px;
        flex-direction: column;
    }
`;

const FormulaDisplay = styled.div`
    padding: 16px;
    border-radius: 8px;
    margin-top: 24px;
    font-size: 13px;
    line-height: 16px;
    color: #333;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        padding: 14px;
        margin-top: 20px;
        font-size: 12px;
        line-height: 15px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        padding: 12px;
        margin-top: 16px;
        font-size: 11px;
        line-height: 14px;
    }
`;

const StyledInput = styled(CommonInput)`
    background: #FFF;
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
    }
`;

const ApplyFormulaButton = styled(BrgButton)`
    gap: 8px;
    border: none;
    font-weight: 500;
    margin-top: 24px;

    &:disabled {
        background: #666;
        cursor: not-allowed;
    }
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        margin-top: 20px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        margin-top: 16px;
        width: 100%;
    }
`;

const ErrorMessage = styled.div`
    color: #E74C3C;
    font-size: 14px;
    margin-top: 12px;
    text-align: center;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 13px;
        margin-top: 10px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        font-size: 12px;
        margin-top: 8px;
    }
`;

const FormulaTitle = styled.h3`
    font-family: 'Inter';
    font-weight: 600;
    font-size: 24px;
    line-height: 29px;
    letter-spacing: -0.48px;
    color: #151514;
    margin: 0 0 24px 0;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 22px;
        line-height: 26px;
        margin: 0 0 20px 0;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        font-size: 20px;
        line-height: 24px;
        margin: 0 0 16px 0;
    }
`;
const Title = styled.h2`
  font-family: 'Inter';
  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  color: #151514;
  margin: 0 0 24px 0;
  text-align: center;
  
  @media (max-width: ${LayoutConst.screen.md}px) {
    font-size: 22px;
    line-height: 26px;
    margin: 0 0 20px 0;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 20px;
    line-height: 24px;
    margin: 0 0 16px 0;
  }
`;

const Description = styled.p`
  font-family: 'Inter';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #666666;
  margin: 0 0 32px 0;
  text-align: center;
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 14px;
    line-height: 20px;
    margin: 0 0 24px 0;
  }
`;
const FormulaSection = styled.div`
  margin-top: 32px;
`;

const FormulaTitleView = styled.h3`
  font-family: 'Inter';
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  color: #151514;
  margin: 0 0 16px 0;
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 16px;
    line-height: 20px;
    margin: 0 0 12px 0;
  }
`;

const FormulaBox = styled.div`
  background: #F8F8F8;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  padding: 20px;
  font-family: 'Inter';
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #151514;
  text-align: center;
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
  }
`;

export const FormulaEditor: FC<{ moduleId: string, expressionName: string, isStudentView?: boolean }> = ({ moduleId, expressionName, isStudentView }) => {
    const { t } = useTranslation();
    const toasts = useToasts();
    
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [formulaData, setFormulaData] = useState<any>(null);
    const [formulaFields, setFormulaFields] = useState<FormulaField[]>([]);
    const [inputValues, setInputValues] = useState<{ [key: string]: string }>({});
    const [generatedId, setGeneratedId] = useState<string | null>(null);
    const [hasValidationError, setHasValidationError] = useState(false);

    const weightFieldMapping: { [key: string]: string } = {
        'attWeight': 'Посещаемость',
        'avgHwWeight': 'Средняя по домашкам',
        'avgSummaryWeight1': 'Промежуточная оценка',
        'avgSummaryWeight2': 'Промежуточная оценка',
        'passHwWeight': 'Доля сданных заданий',
        'examWeight': 'Экзамен',
        'committeeWeight': 'Оценка комиссии'
    };

    const getFormulaByType = (expressionName: string, fields: FormulaField[]) => {
        const weights = fields.reduce((acc, field) => {
            acc[field.fieldKey] = field.weight;
            return acc;
        }, {} as any);

        switch (expressionName) {
            case 'DEFAULT_BACHELORS_DEGREE_WEIGHTS':
                return `Итоговая оценка = 0.25 × (Посещаемость × ${((isNaN(weights.attWeight) ? 0 : weights.attWeight) / 100).toFixed(2).replace('.', ',')} + Промежуточная оценка × ${((isNaN(weights.avgSummaryWeight1) ? 0 : weights.avgSummaryWeight1) / 100).toFixed(2).replace('.', ',')}) + 0.75 × (Промежуточная оценка × ${((isNaN(weights.avgSummaryWeight2) ? 0 : weights.avgSummaryWeight2) / 100).toFixed(2).replace('.', ',')} + Оценка комиссии × ${(((isNaN(weights.committeeWeight) ? 0 : weights.committeeWeight) || (isNaN(weights.examWeight) ? 0 : weights.examWeight)) / 100).toFixed(2).replace('.', ',')})`;
            
            case 'DEFAULT_MASTERS_DEGREE_WEIGHTS':
                return `Итоговая оценка = 0.25 × (Посещаемость × ${((isNaN(weights.attWeight) ? 0 : weights.attWeight) / 100).toFixed(2).replace('.', ',')} + Промежуточная оценка × ${((isNaN(weights.avgSummaryWeight1) ? 0 : weights.avgSummaryWeight1) / 100).toFixed(2).replace('.', ',')}) + 0.75 × (Промежуточная оценка × ${((isNaN(weights.avgSummaryWeight2) ? 0 : weights.avgSummaryWeight2) / 100).toFixed(2).replace('.', ',')} + Оценка комиссии × ${(((isNaN(weights.committeeWeight) ? 0 : weights.committeeWeight) || (isNaN(weights.examWeight) ? 0 : weights.examWeight)) / 100).toFixed(2).replace('.', ',')})`;
            
            case 'DEFAULT_ADDITIONAL_EDUCATION_WEIGHTS':
                return `Итоговая оценка = ${((isNaN(weights.avgHwWeight) ? 0 : weights.avgHwWeight) / 100).toFixed(2).replace('.', ',')} × Средняя по домашкам + ${((isNaN(weights.examWeight) ? 0 : weights.examWeight) / 100).toFixed(2).replace('.', ',')} × Оценка за финальный проект`;
            
            case 'DEFAULT_SHORTINTENSIVE_COURSES_WEIGHTS':
                return `Итоговая оценка = ${((isNaN(weights.avgHwWeight) ? 0 : weights.avgHwWeight) / 100).toFixed(2).replace('.', ',')} × Средняя по домашкам + ${((isNaN(weights.examWeight) ? 0 : weights.examWeight) / 100).toFixed(2).replace('.', ',')} × Экзамен`;
            
            default:
                const validFields = fields.filter(field => field.name.trim() !== '');
                if (validFields.length === 0) return "Итоговая оценка = ";
                
                const formulaParts = validFields.map(field => {
                    const weight = isNaN(field.weight) ? 0 : field.weight;
                    return `${field.name} × ${(weight / 100).toFixed(2).replace('.', ',')}`;
                });
                
                return `Итоговая оценка = ${formulaParts.join(' + ')}`;
        }
    };

    const handleApplyFormula = () => {
        if (!formulaData) return;
        
        setIsLoading(true);
        
        const requestData: any = {
            id: generatedId ? generatedId : undefined,
            moduleId: moduleId,
            customWeights: formulaData.customWeights || {},
            expressionType: formulaData.expressionType || 0
        };
        
        formulaFields.forEach(field => {
            requestData[field.fieldKey] = field.weight;
        });
        
        API.request()
            .endpoint(Endpoints.geograffee)
            .url("/api/LMS/SaveExpressionGrade")
            .post()
            .body(requestData)
            .release();
        
        toasts.info('Формула успешно применена');
        setIsLoading(false);
    };

   const handleWeightChange = (id: string, value: string) => {
    if (value === '' || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
        setInputValues(prev => ({
            ...prev,
            [id]: value
        }));
        
        const numValue = value === '' ? 0 : parseFloat(value.replace(',', '.')) || 0;
        
        // Проверка диапазона от 1 до 99
        const isValid = numValue >= 1 && numValue <= 99;
        setHasValidationError(!isValid);
        
        
        setFormulaFields(prev => 
            prev.map(field => 
                field.id === id 
                    ? { ...field, weight: numValue }
                    : field
            )
        );
    }
};

    const handleNameChange = (id: string, value: string) => {
        setFormulaFields(prev => 
            prev.map(field => 
                field.id === id 
                    ? { ...field, name: value }
                    : field
            )
        );
    };

    const generatedFormula = useMemo(() => {
        if (!expressionName) return "Итоговая оценка = ";
        
        return getFormulaByType(expressionName, formulaFields);
    }, [formulaData, formulaFields]);

   const loadFormulaData = () => {
    if (!moduleId) return;
    
    API.request()
        .endpoint(Endpoints.geograffee)
        .url("/api/LMS/GetExpressionGradeByModuleId")
        .get()
        .addQuery("moduleId", moduleId)
        .send()
        .then((r) => {
            if (!r.ok) {
                throw new Error(`HTTP ${r.status}: ${r.statusText}`);
            }
            return r.json();
        })
        .then((r) => {
            setFormulaData(r);
            setIsInitialLoading(false);
            setGeneratedId(r.id);
        })
        .catch((e) => {  
            API.request()
                .endpoint(Endpoints.geograffee)
                .url("/api/LMS/GetExpressionGradeByName")
                .get()
                .addQuery("gradeName", expressionName)
                .send()
                .then((r) => {
                    if (!r.ok) {
                        throw new Error(`HTTP ${r.status}: ${r.statusText}`);
                    }
                    return r.json();
                })
                .then((r) => {
                    console.log('Fallback формула загружена:', r);
                    setFormulaData(r);
                    setIsInitialLoading(false);
                    setGeneratedId(null);
                })
                .catch((fallbackError) => {
                    console.error('Ошибка при fallback запросе:', fallbackError);
                    setIsInitialLoading(false);
                });
        });
};

    useEffect(() => {
        if (moduleId) {
            setIsInitialLoading(true);
            loadFormulaData();
        }
    }, [moduleId]);

    useEffect(() => {
        if (formulaData) {
            const fields: FormulaField[] = [];
            const values: { [key: string]: string } = {};
            let fieldId = 1;

            Object.entries(weightFieldMapping).forEach(([fieldKey, fieldName]) => {
                const weight = formulaData[fieldKey] || 0;
                if (weight > 0) {
                    const id = fieldId.toString();
                    fields.push({
                        id,
                        name: fieldName,
                        weight,
                        fieldKey
                    });
                    values[id] = weight.toString().replace('.', ',');
                    fieldId++;
                }
            });

            if (fields.length === 0) {
                fields.push({
                    id: '1',
                    name: 'Средняя по домашкам',
                    weight: 0,
                    fieldKey: 'avgHwWeight'
                });
                values['1'] = '0';
            }

            setFormulaFields(fields);
            setInputValues(values);
        }
    }, [formulaData]);

    if (!moduleId) return null;

    if (isInitialLoading) {
        return (
            <LoaderSpinner color={'#909090'}/>
        );
    }

    if (isStudentView) {
        return (
            <FormulaWrapper>
                <Title>
                    {t('project_edit.lms.grade.title')}
                </Title>
                
                <Description>
                    {t('project_edit.lms.grade.description')}
                </Description>
                
                <FormulaSection>
                    <FormulaTitleView>
                    {t('project_edit.lms.grade.formula_title')}
                    </FormulaTitleView>
                    
                    <FormulaBox>
                        {generatedFormula}
                    </FormulaBox>
                </FormulaSection>
            </FormulaWrapper>
        )
    }

    return (
        <FormulaWrapper>
            <FormulaTitle>{t('teams.adminPage.fields.course_formula')}</FormulaTitle>
            
            <TableHeader>
                <HeaderLabel style={{ flex: '1' }}>Переменная</HeaderLabel>
                <HeaderLabel style={{ width: '150px' }}>Вес (%)</HeaderLabel>
            </TableHeader>
            
            {formulaFields.map((field) => (
                <TableRow key={field.id}>
                    <div style={{ flex: '1' }}>
                        <StyledInput 
                            type={INPUT_TYPES.text}
                            value={field.name}
                            onChange={(ev: any) => handleNameChange(field.id, ev.target.value)}
                            disable={true}
                        />
                    </div>
                    <div style={{ width: '150px' }}>
                        <StyledInput 
                            type={INPUT_TYPES.text}
                            value={inputValues[field.id]}
                            onChange={(ev: any) => handleWeightChange(field.id, ev.target.value)}
                        />
                    </div>
                </TableRow>
            ))}
            
            <FormulaDisplay>
                {generatedFormula}
            </FormulaDisplay>
            
            <FlexBox align='flex-end' gx={16}>
            <ApplyFormulaButton 
                variant={ButtonVariant.Filled}
                onClick={handleApplyFormula}
                disabled={isLoading || hasValidationError}
                >
                {isLoading ? 'Сохранение...' : 'Применить формулу'}
            </ApplyFormulaButton>
            
            {hasValidationError && (
                <ErrorMessage>
                    Введите корректное значение (от 1 до 99)
                </ErrorMessage>
            )}
            </FlexBox>
        </FormulaWrapper>
    );
};
import { FC, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { API, Endpoints } from '@brg/api';
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useToasts } from '@brg/ui';
import { LoaderSpinner } from 'shared/modals/artzLegalsModal/LoaderAnimation';
import { LayoutConst } from 'core';

interface FormulaField {
    id: string;
    name: string;
    weight: number;
    fieldKey: string;
}

const FormulaWrapper = styled.div`
    background: #F2F2F2;
    padding: 24px;
    border-radius: 8px;
    margin-top: 24px;
    min-width: 888px;
    .ui-input_container{
       background: #FFF; 
    }
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        padding: 20px;
        min-width: auto;
        margin-top: 20px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        padding: 16px;
        margin-top: 16px;
    }
`;

const TableHeader = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 12px;
        margin-bottom: 14px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        gap: 8px;
        margin-bottom: 12px;
        flex-direction: column;
    }
`;

const HeaderLabel = styled.div`
    font-size: 14px;
    color: #666;
    font-weight: 500;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 13px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        font-size: 12px;
    }
`;

const TableRow = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 12px;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 12px;
        margin-bottom: 10px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        gap: 8px;
        margin-bottom: 8px;
        flex-direction: column;
    }
`;

const FormulaDisplay = styled.div`
    padding: 16px;
    border-radius: 8px;
    margin-top: 24px;
    font-size: 13px;
    line-height: 16px;
    color: #333;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        padding: 14px;
        margin-top: 20px;
        font-size: 12px;
        line-height: 15px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        padding: 12px;
        margin-top: 16px;
        font-size: 11px;
        line-height: 14px;
    }
`;

const StyledInput = styled(CommonInput)`
    background: #FFF;
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
    }
`;

const ApplyFormulaButton = styled(BrgButton)`
    gap: 8px;
    border: none;
    font-weight: 500;
    margin-top: 24px;

    &:disabled {
        background: #666;
        cursor: not-allowed;
    }
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        margin-top: 20px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        margin-top: 16px;
        width: 100%;
    }
`;

const ErrorMessage = styled.div`
    color: #E74C3C;
    font-size: 14px;
    margin-top: 12px;
    text-align: center;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 13px;
        margin-top: 10px;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        font-size: 12px;
        margin-top: 8px;
    }
`;

const FormulaTitle = styled.h3`
    font-family: 'Inter';
    font-weight: 600;
    font-size: 24px;
    line-height: 29px;
    letter-spacing: -0.48px;
    color: #151514;
    margin: 0 0 24px 0;
    
    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 22px;
        line-height: 26px;
        margin: 0 0 20px 0;
    }
    
    @media (max-width: ${LayoutConst.screen.s}px) {
        font-size: 20px;
        line-height: 24px;
        margin: 0 0 16px 0;
    }
`;
const Title = styled.h2`
  font-family: 'Inter';
  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  color: #151514;
  margin: 0 0 24px 0;
  text-align: center;
  
  @media (max-width: ${LayoutConst.screen.md}px) {
    font-size: 22px;
    line-height: 26px;
    margin: 0 0 20px 0;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 20px;
    line-height: 24px;
    margin: 0 0 16px 0;
  }
`;

const Description = styled.p`
  font-family: 'Inter';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #666666;
  margin: 0 0 32px 0;
  text-align: center;
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 14px;
    line-height: 20px;
    margin: 0 0 24px 0;
  }
`;
const FormulaSection = styled.div`
  margin-top: 32px;
`;

const FormulaTitleView = styled.h3`
  font-family: 'Inter';
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  color: #151514;
  margin: 0 0 16px 0;
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 16px;
    line-height: 20px;
    margin: 0 0 12px 0;
  }
`;

const FormulaBox = styled.div`
  background: #F8F8F8;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  padding: 20px;
  font-family: 'Inter';
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #151514;
  text-align: center;
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
  }
`;

export const FormulaEditor: FC<{ moduleId: string, expressionName: string, isStudentView?: boolean }> = ({ moduleId, expressionName, isStudentView }) => {
    const { t } = useTranslation();
    const toasts = useToasts();
    
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [formulaData, setFormulaData] = useState<any>(null);
    const [formulaFields, setFormulaFields] = useState<FormulaField[]>([]);
    const [inputValues, setInputValues] = useState<{ [key: string]: string }>({});
    const [generatedId, setGeneratedId] = useState<string | null>(null);
    const [hasValidationError, setHasValidationError] = useState(false);

    const weightFieldMapping: { [key: string]: string } = {
        'attWeight': 'Посещаемость',
        'avgHwWeight': 'Средняя по домашкам',
        'avgSummaryWeight1': 'Промежуточная оценка',
        'avgSummaryWeight2': 'Промежуточная оценка',
        'passHwWeight': 'Доля сданных заданий',
        'examWeight': 'Экзамен',
        'committeeWeight': 'Оценка комиссии'
    };

    const getFormulaByType = (expressionName: string, fields: FormulaField[]) => {
        const weights = fields.reduce((acc, field) => {
            acc[field.fieldKey] = field.weight;
            return acc;
        }, {} as any);

        switch (expressionName) {
            case 'DEFAULT_BACHELORS_DEGREE_WEIGHTS':
                return `Итоговая оценка = 0.25 × (Посещаемость × ${((isNaN(weights.attWeight) ? 0 : weights.attWeight) / 100).toFixed(2).replace('.', ',')} + Промежуточная оценка × ${((isNaN(weights.avgSummaryWeight1) ? 0 : weights.avgSummaryWeight1) / 100).toFixed(2).replace('.', ',')}) + 0.75 × (Промежуточная оценка × ${((isNaN(weights.avgSummaryWeight2) ? 0 : weights.avgSummaryWeight2) / 100).toFixed(2).replace('.', ',')} + Оценка комиссии × ${(((isNaN(weights.committeeWeight) ? 0 : weights.committeeWeight) || (isNaN(weights.examWeight) ? 0 : weights.examWeight)) / 100).toFixed(2).replace('.', ',')})`;
            
            case 'DEFAULT_MASTERS_DEGREE_WEIGHTS':
                return `Итоговая оценка = 0.25 × (Посещаемость × ${((isNaN(weights.attWeight) ? 0 : weights.attWeight) / 100).toFixed(2).replace('.', ',')} + Промежуточная оценка × ${((isNaN(weights.avgSummaryWeight1) ? 0 : weights.avgSummaryWeight1) / 100).toFixed(2).replace('.', ',')}) + 0.75 × (Промежуточная оценка × ${((isNaN(weights.avgSummaryWeight2) ? 0 : weights.avgSummaryWeight2) / 100).toFixed(2).replace('.', ',')} + Оценка комиссии × ${(((isNaN(weights.committeeWeight) ? 0 : weights.committeeWeight) || (isNaN(weights.examWeight) ? 0 : weights.examWeight)) / 100).toFixed(2).replace('.', ',')})`;
            
            case 'DEFAULT_ADDITIONAL_EDUCATION_WEIGHTS':
                return `Итоговая оценка = ${((isNaN(weights.avgHwWeight) ? 0 : weights.avgHwWeight) / 100).toFixed(2).replace('.', ',')} × Средняя по домашкам + ${((isNaN(weights.examWeight) ? 0 : weights.examWeight) / 100).toFixed(2).replace('.', ',')} × Оценка за финальный проект`;
            
            case 'DEFAULT_SHORTINTENSIVE_COURSES_WEIGHTS':
                return `Итоговая оценка = ${((isNaN(weights.avgHwWeight) ? 0 : weights.avgHwWeight) / 100).toFixed(2).replace('.', ',')} × Средняя по домашкам + ${((isNaN(weights.examWeight) ? 0 : weights.examWeight) / 100).toFixed(2).replace('.', ',')} × Экзамен`;
            
            default:
                const validFields = fields.filter(field => field.name.trim() !== '');
                if (validFields.length === 0) return "Итоговая оценка = ";
                
                const formulaParts = validFields.map(field => {
                    const weight = isNaN(field.weight) ? 0 : field.weight;
                    return `${field.name} × ${(weight / 100).toFixed(2).replace('.', ',')}`;
                });
                
                return `Итоговая оценка = ${formulaParts.join(' + ')}`;
        }
    };

    const handleApplyFormula = () => {
        if (!formulaData) return;
        
        setIsLoading(true);
        
        const requestData: any = {
            id: generatedId ? generatedId : undefined,
            moduleId: moduleId,
            customWeights: formulaData.customWeights || {},
            expressionType: formulaData.expressionType || 0
        };
        
        formulaFields.forEach(field => {
            requestData[field.fieldKey] = field.weight;
        });
        
        API.request()
            .endpoint(Endpoints.geograffee)
            .url("/api/LMS/SaveExpressionGrade")
            .post()
            .body(requestData)
            .release();
        
        toasts.info('Формула успешно применена');
        setIsLoading(false);
    };

   const handleWeightChange = (id: string, value: string) => {
    if (value === '' || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
        setInputValues(prev => ({
            ...prev,
            [id]: value
        }));
        
        const numValue = value === '' ? 0 : parseFloat(value.replace(',', '.')) || 0;
        
        // Проверка диапазона от 1 до 99
        const isValid = numValue >= 1 && numValue <= 99;
        setHasValidationError(!isValid);
        
        
        setFormulaFields(prev => 
            prev.map(field => 
                field.id === id 
                    ? { ...field, weight: numValue }
                    : field
            )
        );
    }
};

    const handleNameChange = (id: string, value: string) => {
        setFormulaFields(prev => 
            prev.map(field => 
                field.id === id 
                    ? { ...field, name: value }
                    : field
            )
        );
    };

    const generatedFormula = useMemo(() => {
        if (!expressionName) return "Итоговая оценка = ";
        
        return getFormulaByType(expressionName, formulaFields);
    }, [formulaData, formulaFields]);

   const loadFormulaData = () => {
    if (!moduleId) return;
    
    API.request()
        .endpoint(Endpoints.geograffee)
        .url("/api/LMS/GetExpressionGradeByModuleId")
        .get()
        .addQuery("moduleId", moduleId)
        .send()
        .then((r) => {
            if (!r.ok) {
                throw new Error(`HTTP ${r.status}: ${r.statusText}`);
            }
            return r.json();
        })
        .then((r) => {
            setFormulaData(r);
            setIsInitialLoading(false);
            setGeneratedId(r.id);
        })
        .catch((e) => {  
            API.request()
                .endpoint(Endpoints.geograffee)
                .url("/api/LMS/GetExpressionGradeByName")
                .get()
                .addQuery("gradeName", expressionName)
                .send()
                .then((r) => {
                    if (!r.ok) {
                        throw new Error(`HTTP ${r.status}: ${r.statusText}`);
                    }
                    return r.json();
                })
                .then((r) => {
                    console.log('Fallback формула загружена:', r);
                    setFormulaData(r);
                    setIsInitialLoading(false);
                    setGeneratedId(null);
                })
                .catch((fallbackError) => {
                    console.error('Ошибка при fallback запросе:', fallbackError);
                    setIsInitialLoading(false);
                });
        });
};

    useEffect(() => {
        if (moduleId) {
            setIsInitialLoading(true);
            loadFormulaData();
        }
    }, [moduleId]);

    useEffect(() => {
        if (formulaData) {
            const fields: FormulaField[] = [];
            const values: { [key: string]: string } = {};
            let fieldId = 1;

            Object.entries(weightFieldMapping).forEach(([fieldKey, fieldName]) => {
                const weight = formulaData[fieldKey] || 0;
                if (weight > 0) {
                    const id = fieldId.toString();
                    fields.push({
                        id,
                        name: fieldName,
                        weight,
                        fieldKey
                    });
                    values[id] = weight.toString().replace('.', ',');
                    fieldId++;
                }
            });

            if (fields.length === 0) {
                fields.push({
                    id: '1',
                    name: 'Средняя по домашкам',
                    weight: 0,
                    fieldKey: 'avgHwWeight'
                });
                values['1'] = '0';
            }

            setFormulaFields(fields);
            setInputValues(values);
        }
    }, [formulaData]);

    if (!moduleId) return null;

    if (isInitialLoading) {
        return (
            <LoaderSpinner color={'#909090'}/>
        );
    }

    if (isStudentView) {
        return (
            <FormulaWrapper>
                <Title>
                    {t('project_edit.lms.grade.title')}
                </Title>
                
                <Description>
                    {t('project_edit.lms.grade.description')}
                </Description>
                
                <FormulaSection>
                    <FormulaTitleView>
                    {t('project_edit.lms.grade.formula_title')}
                    </FormulaTitleView>
                    
                    <FormulaBox>
                        {generatedFormula}
                    </FormulaBox>
                </FormulaSection>
            </FormulaWrapper>
        )
    }

    return (
        <FormulaWrapper>
            <FormulaTitle>{t('teams.adminPage.fields.course_formula')}</FormulaTitle>
            
            <TableHeader>
                <HeaderLabel style={{ flex: '1' }}>Переменная</HeaderLabel>
                <HeaderLabel style={{ width: '150px' }}>Вес (%)</HeaderLabel>
            </TableHeader>
            
            {formulaFields.map((field) => (
                <TableRow key={field.id}>
                    <div style={{ flex: '1' }}>
                        <StyledInput 
                            type={INPUT_TYPES.text}
                            value={field.name}
                            onChange={(ev: any) => handleNameChange(field.id, ev.target.value)}
                            disable={true}
                        />
                    </div>
                    <div style={{ width: '150px' }}>
                        <StyledInput 
                            type={INPUT_TYPES.text}
                            value={inputValues[field.id]}
                            onChange={(ev: any) => handleWeightChange(field.id, ev.target.value)}
                        />
                    </div>
                </TableRow>
            ))}
            
            <FormulaDisplay>
                {generatedFormula}
            </FormulaDisplay>
            
            <FlexBox align='flex-end' gx={16}>
            <ApplyFormulaButton 
                variant={ButtonVariant.Filled}
                onClick={handleApplyFormula}
                disabled={isLoading || hasValidationError}
                >
                {isLoading ? 'Сохранение...' : 'Применить формулу'}
            </ApplyFormulaButton>
            
            {hasValidationError && (
                <ErrorMessage>
                    Введите корректное значение (от 1 до 99)
                </ErrorMessage>
            )}
            </FlexBox>
        </FormulaWrapper>
    );
};
